/**
 * useRealtimeSocket
 *
 * Connects to the Django Channels WebSocket endpoint and delivers
 * real-time model events to the caller.
 *
 * Protocol: ws(s)://<host>/ws/realtime/?token=<JWT>
 * Server pushes JSON frames with shape:
 *   { type, model, action, id, data, location_id, schema, timestamp }
 *
 * Implements automatic keepalive (ping/pong) every 30s to prevent
 * idle disconnections from proxies.
 *
 * See apps/realtime/REALTIME_PROTOCOL.md for full spec.
 */

import { useEffect, useRef, useCallback, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/core';
import { API_BASE_URL } from '@/shared/services/api';

export interface RealtimeMessage {
  type: string;
  model?: string;
  action?: 'created' | 'updated' | 'deleted' | string;
  id?: string;
  data?: Record<string, unknown>;
  location_id?: string;
  schema?: string;
  timestamp?: string;
}

export type RealtimeMessageHandler = (msg: RealtimeMessage) => void;
export type ConnectionState = 'connected' | 'disconnected' | 'reconnecting';

// FIX-1.3: in-memory pub/sub for additional consumers that want to
// react to realtime messages WITHOUT opening another WebSocket.
// The single subscription mounted in AppLayout (via
// useRealtimeInvalidation → useRealtimeSocket) publishes every frame
// here, so feature hooks like usePlaidAutoRefresh can hang off this
// registry instead of opening their own socket.
const realtimeBusListeners = new Set<RealtimeMessageHandler>();

function publishToBus(msg: RealtimeMessage) {
  for (const listener of realtimeBusListeners) {
    try {
      listener(msg);
    } catch (err) {
      // One bad subscriber must not take the rest down.
      // eslint-disable-next-line no-console
      console.error('[useRealtimeMessage] handler threw', err);
    }
  }
}

/**
 * Test-only hook into the realtime bus. Production code MUST NOT
 * call this — it bypasses every guard the WebSocket layer applies.
 * Exported so unit tests can drive `useRealtimeMessage` without a
 * live WebSocket. The double-underscore prefix is the convention
 * for "do not call from app code".
 */
export function __publishToRealtimeBusForTests(msg: RealtimeMessage): void {
  publishToBus(msg);
}

// ── Global connection-state store ──────────────────────────────────────────
// The single socket mounted in AppLayout owns the live connection state, but
// feature hooks (booking/guest queries) need to read it WITHOUT opening their
// own socket — same rationale as the message bus above. The socket publishes
// every state transition here; `useRealtimeConnectionState` subscribes.
let currentConnectionState: ConnectionState = 'disconnected';
const connectionStateListeners = new Set<() => void>();

function setGlobalConnectionState(state: ConnectionState) {
  if (state === currentConnectionState) return;
  currentConnectionState = state;
  for (const listener of connectionStateListeners) listener();
}

function subscribeConnectionState(listener: () => void) {
  connectionStateListeners.add(listener);
  return () => {
    connectionStateListeners.delete(listener);
  };
}

/**
 * Read the live realtime connection state from anywhere in the tree without
 * opening a second socket. Reflects the single AppLayout subscription.
 */
export function useRealtimeConnectionState(): ConnectionState {
  return useSyncExternalStore(
    subscribeConnectionState,
    () => currentConnectionState,
  );
}

/**
 * React Query `refetchInterval` value that acts ONLY as a fallback: returns
 * `false` (no polling) while the WebSocket is connected — realtime
 * invalidation already keeps caches fresh — and a polling interval when it is
 * disconnected/reconnecting, so pages still recover automatically if the
 * socket is down. Pass to latency-sensitive queries (booking/guest).
 */
export function useRealtimeFallbackInterval(ms = 20_000): number | false {
  const state = useRealtimeConnectionState();
  return state === 'connected' ? false : ms;
}

/**
 * Subscribe to realtime messages from the GLOBAL `useRealtimeSocket`
 * subscription (mounted once in AppLayout). Does NOT open another
 * WebSocket — call this in any feature hook that just wants to react
 * to a slice of the firehose.
 *
 * The handler is wrapped in a stable ref so callers can pass an
 * inline arrow function without re-subscribing on every render.
 */
export function useRealtimeMessage(handler: RealtimeMessageHandler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const listener: RealtimeMessageHandler = (msg) => handlerRef.current(msg);
    realtimeBusListeners.add(listener);
    return () => {
      realtimeBusListeners.delete(listener);
    };
  }, []);
}

// Derive from the same base URL as the HTTP API so both always point to the same host.
// https://... → wss://...  |  http://... → ws://...
const WS_BASE_URL = API_BASE_URL.replace(/^http/, 'ws');

const MAX_RETRIES = 8;
const BASE_DELAY_MS = 1000;
const KEEPALIVE_INTERVAL_MS = 30_000;
const PONG_TIMEOUT_MS = 10_000;

/**
 * Opens a WebSocket to /ws/realtime/ and calls `onMessage` for every
 * server-pushed frame. Reconnects automatically with exponential back-off.
 *
 * D4 — Implements automatic ping/pong keepalive every 30s with 10s timeout.
 * D6 — Exposes connectionState for UI indicators.
 *
 * @returns A ref to the active WebSocket, send function, and connection state
 */
export function useRealtimeSocket(
  onMessage: RealtimeMessageHandler,
  options?: { enabled?: boolean; onReconnect?: () => void },
) {
  const { isAuthenticated } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const retriesRef = useRef(0);
  const isMountedRef = useRef(true);
  const keepaliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pongTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep onMessage stable so the effect doesn't re-run on every render
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onReconnectRef = useRef(options?.onReconnect);
  onReconnectRef.current = options?.onReconnect;

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const enabled = options?.enabled ?? true;

  // Update both the local state (for this hook's return value) and the
  // module-level store (read app-wide via useRealtimeConnectionState).
  const updateConnectionState = useCallback((state: ConnectionState) => {
    setConnectionState(state);
    setGlobalConnectionState(state);
  }, []);

  const clearKeepAliveTasks = useCallback(() => {
    if (keepaliveIntervalRef.current) {
      clearInterval(keepaliveIntervalRef.current);
      keepaliveIntervalRef.current = null;
    }
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  const startKeepalive = useCallback((ws: WebSocket) => {
    clearKeepAliveTasks();

    const sendPing = () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));

        // D4: Set 10s timeout to receive pong
        pongTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            ws.close();
            updateConnectionState('disconnected');
          }
        }, PONG_TIMEOUT_MS);
      }
    };

    // D4: Send ping every 30s
    keepaliveIntervalRef.current = setInterval(sendPing, KEEPALIVE_INTERVAL_MS);
  }, [clearKeepAliveTasks, updateConnectionState]);

  const connect = useCallback(() => {
    if (!isAuthenticated || !enabled || !isMountedRef.current) return;

    updateConnectionState('reconnecting');
    const url = `${WS_BASE_URL}/ws/realtime/`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!isMountedRef.current) return;
      const wasReconnect = retriesRef.current > 0;
      retriesRef.current = 0;
      updateConnectionState('connected');

      // D4: Start keepalive on open
      startKeepalive(ws);

      if (wasReconnect) {
        onReconnectRef.current?.();
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string) as RealtimeMessage;

        // D4: Clear pong timeout on any incoming message (not just pong)
        if (pongTimeoutRef.current) {
          clearTimeout(pongTimeoutRef.current);
          pongTimeoutRef.current = null;
        }

        onMessageRef.current(msg);
        // FIX-1.3: fan out every frame to the in-memory bus so
        // feature hooks subscribed via useRealtimeMessage receive it
        // without opening their own socket.
        publishToBus(msg);
      } catch {
        // Ignore malformed frames
      }
    };

    ws.onclose = (event: CloseEvent) => {
      wsRef.current = null;
      clearKeepAliveTasks();
      updateConnectionState('disconnected');

      // 4001 = no token, 4002 = invalid token — don't retry, token refresh needed
      if (event.code === 4001 || event.code === 4002) return;
      if (!isMountedRef.current) return;
      if (retriesRef.current >= MAX_RETRIES) return;

      const delay = Math.min(BASE_DELAY_MS * 2 ** retriesRef.current, 30_000);
      retriesRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      // onclose will fire after onerror; reconnect logic lives there
    };
  }, [enabled, isAuthenticated, startKeepalive, clearKeepAliveTasks, updateConnectionState]);

  useEffect(() => {
    isMountedRef.current = true;
    connect();

    // D7: Listen for token refresh events from AuthContext via postMessage
    const handleTokenRefresh = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'token-refreshed') {
        // Close current connection and reconnect with new token
        wsRef.current?.close();
        reconnectTimeoutRef.current = setTimeout(connect, 100);
      }
    };

    window.addEventListener('message', handleTokenRefresh);

    return () => {
      isMountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      wsRef.current?.close();
      clearKeepAliveTasks();
      window.removeEventListener('message', handleTokenRefresh);
    };
  }, [connect, clearKeepAliveTasks]);

  /** Send a JSON message to the server */
  const send = useCallback((payload: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }, []);

  return { wsRef, send, connectionState };
}
